import { Delete, Edit, ExpandLess, ExpandMore, NotificationsActive, PentagonOutlined, TimeToLeaveRounded } from '@mui/icons-material';
import { Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { ICompany } from 'app/shared/model/company.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { ITruckGroupListDTO } from 'app/shared/model/truck-group.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { DEFAULT_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSortState } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { TruckGroupDetailView, TruckGroupDetailsPanel } from './components/TruckGroupDetailsPanel';
import { clearEntities, deleteEntity, getEntities } from './truck-group.reducer';

export const TruckGroup = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));

  const truckGroupList = useAppSelector(state => state.truckGroup.entities) as ITruckGroupListDTO[];
  const loading = useAppSelector(state => state.truckGroup.loading);

  const canManage = isAdmin || isManufacturerAdmin || isTransporterAdmin;

  const urlParams = new URLSearchParams(props.location.search);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(() => {
    const id = urlParams.get('companyId');
    const name = urlParams.get('companyName');
    return id && name ? { id: Number(id), name } : null;
  });
  const [searchCompanyName, setSearchCompanyName] = useState(() => urlParams.get('companyName') || '');
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [activeViewByGroupId, setActiveViewByGroupId] = useState<Record<number, TruckGroupDetailView>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ITruckGroupListDTO | null>(null);

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        companyId: selectedCompany?.id,
      })
    );
  }, [paginationState, selectedCompany]);

  useEffect(() => {
    dispatch(clearEntities());
    getAllEntities();
  }, [getAllEntities]);

  const setActivePage = () => {
    setPaginationState(prev => ({ ...prev, activePage: 1 }));
  };

  const handleSearchCompany = (company: ICompany) => {
    setSelectedCompany(company);
    setSearchCompanyName(company?.name ?? '');
    setActivePage();
    const params = new URLSearchParams(props.location.search);
    if (company?.id) {
      params.set('companyId', String(company.id));
      params.set('companyName', company.name ?? '');
    } else {
      params.delete('companyId');
      params.delete('companyName');
    }
    props.history.replace(`${props.location.pathname}?${params.toString()}`);
  };

  const sortClear = () => {
    setSelectedCompany(null);
    setSearchCompanyName('');
    setActivePage();
    const params = new URLSearchParams(props.location.search);
    params.delete('companyId');
    params.delete('companyName');
    props.history.replace(`${props.location.pathname}?${params.toString()}`);
  };

  const sortedGroups = useMemo(() => {
    if (!truckGroupList?.length) return [];
    const defaultGroup = truckGroupList.find(g => g.isDefault);
    const others = truckGroupList.filter(g => !g.isDefault);
    return defaultGroup ? [defaultGroup, ...others] : others;
  }, [truckGroupList]);

  const handleRowClick = (groupId: number) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }

    setActiveViewByGroupId(current => ({ ...current, [groupId]: current[groupId] ?? 'trucks' }));
    setExpandedGroupId(groupId);
  };

  const handleSummaryClick = (event: React.MouseEvent, groupId: number, view: TruckGroupDetailView) => {
    event.stopPropagation();
    setExpandedGroupId(groupId);
    setActiveViewByGroupId(prev => ({ ...prev, [groupId]: view }));
  };

  const renderSummaryButton = (group: ITruckGroupListDTO, view: TruckGroupDetailView, icon: React.ReactElement<any>, label: string, count: number) => {
    if (!group.id) return null;

    const isActive = expandedGroupId === group.id && (activeViewByGroupId[group.id] ?? 'trucks') === view;

    return (
      <Box
        component="button"
        type="button"
        onClick={event => handleSummaryClick(event, group.id, view)}
        sx={{
          appearance: 'none',
          border: `1px solid ${isActive ? theme.palette.secondary.main : alpha(theme.palette.text.primary, 0.12)}`,
          backgroundColor: isActive ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
          borderRadius: '8px',
          color: isActive ? theme.palette.secondary.main : theme.palette.text.secondary,
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          columnGap: 0.75,
          minHeight: 38,
          minWidth: { xs: '100%', sm: 108 },
          px: 1.25,
          py: 0.5,
          textAlign: 'left',
          transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
          '&:hover': {
            borderColor: theme.palette.secondary.main,
            backgroundColor: alpha(theme.palette.secondary.main, 0.06),
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        <Box minWidth={0} display="flex" alignItems="center" gap={0.75}>
          <Typography variant="caption" color="inherit" noWrap sx={{ fontSize: '0.72rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.primary" fontWeight={700} lineHeight={1} noWrap>
            {count}
          </Typography>
        </Box>
      </Box>
    );
  };

  const buildEditUrl = (group: ITruckGroupListDTO) => {
    const params = new URLSearchParams(props.location.search);
    if (isAdmin && group.companyId) {
      params.set('companyId', String(group.companyId));
      if (group.companyName) {
        params.set('companyName', group.companyName);
      }
    }
    const queryString = params.toString();
    return `/truck-group/${group.id}/edit${queryString ? `?${queryString}` : ''}`;
  };

  const handleDeleteClick = (group: ITruckGroupListDTO) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (groupToDelete?.id) {
      dispatch(deleteEntity({ id: groupToDelete.id, companyId: selectedCompany?.id }));
      if (expandedGroupId === groupToDelete.id) {
        setExpandedGroupId(null);
      }
    }
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', padding: '24px' }}>
      <PageHeader
        headingId="truck-group-heading"
        headingDataCy="TruckGroupHeading"
        contentKey="fleetSenseApp.truckGroup.home.title"
        buttonTextKey="fleetSenseApp.truckGroup.home.createLabel"
        match={match}
        urlButton={`new${props.location.search}`}
        showButton={canManage}
      />

      {isAdmin && (
        <PageFilters
          setActivePage={setActivePage}
          isAdmin={isAdmin}
          searchCompanyName={searchCompanyName}
          handleSearchCompany={handleSearchCompany}
          getAllEntities={getAllEntities}
          sortClear={sortClear}
          status={StatusFilter.ACTIVATED}
          showFilters={['company', 'clearFilters']}
          onlyTransporters={true}
          loading={loading}
        />
      )}

      {loading ? (
        <Loading />
      ) : sortedGroups.length > 0 ? (
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          {sortedGroups.map((group: ITruckGroupListDTO) => {
            const isExpanded = expandedGroupId === group.id;
            const activeView = group.id ? activeViewByGroupId[group.id] ?? 'trucks' : 'trucks';

            return (
              <Box
                key={group.id}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[1],
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'scale(1.005)' },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={2.5}
                  pt={2}
                  pb={1.5}
                  sx={{ cursor: group.id ? 'pointer' : 'default' }}
                  onClick={() => group.id && handleRowClick(group.id)}
                >
                  <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} minWidth={0} flex={1}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', flexShrink: 0, mt: { xs: 0.25, sm: 0 } }}>
                      {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                    <Box
                      display="flex"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      gap={{ xs: 0.5, sm: 0.75 }}
                      minWidth={0}
                      flex={1}
                    >
                      <Tooltip title={group.name || ''}>
                        <Typography variant="subtitle1" color="primary.contrastText" fontWeight={600} noWrap sx={{ minWidth: 0, maxWidth: '100%' }}>
                          {group.name}
                        </Typography>
                      </Tooltip>
                      <Box display="flex" alignItems="center" gap={0.5} minWidth={0} maxWidth="100%">
                        {group.isDefault && <Chip label="Padrão" size="small" color="secondary" variant="outlined" sx={{ flexShrink: 0 }} />}
                        {isAdmin && group.companyName && (
                          <Chip
                            label={group.companyName}
                            size="small"
                            variant="outlined"
                            sx={{
                              opacity: 0.7,
                              flexShrink: 1,
                              maxWidth: { xs: 150, sm: 220 },
                              minWidth: 0,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" gap={0.5} flexShrink={0} onClick={e => e.stopPropagation()}>
                    {canManage && (
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => props.history.push(buildEditUrl(group))}>
                          <Edit color="secondary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && !group.isDefault && (
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteClick(group)}>
                          <Delete color="error" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {group.description && (
                  <Typography variant="body2" color="text.secondary" px={2.5} pb={1.5}>
                    {group.description}
                  </Typography>
                )}

                <Divider sx={{ opacity: 0.1 }} />

                <Box display="flex" flexWrap="wrap" gap={1.25} px={2.5} py={1.5}>
                  {renderSummaryButton(group, 'trucks', <TimeToLeaveRounded />, 'Ativos', group.truckCount ?? 0)}
                  {renderSummaryButton(group, 'alerts', <NotificationsActive />, 'Alertas', group.alertCount ?? 0)}
                  {renderSummaryButton(group, 'fences', <PentagonOutlined />, 'Cercas', group.virtualFenceCount ?? 0)}
                </Box>

                {group.id && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ opacity: 0.1 }} />
                    <Box pt={1.5}>
                      <Typography variant="caption" color="text.secondary" px={2.5} pb={1} display="block" fontWeight={600}>
                        {activeView === 'trucks' && 'Ativos do grupo'}
                        {activeView === 'alerts' && 'Alertas do grupo'}
                        {activeView === 'fences' && 'Cercas do grupo'}
                      </Typography>
                      <TruckGroupDetailsPanel groupId={group.id} activeView={activeView} />
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        !loading && <NoData title="Nenhum grupo encontrado" />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
      >
        <DialogTitle>
          <Typography variant="h6">Excluir grupo de ativos</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Tem certeza que deseja excluir o grupo <strong>{groupToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Os ativos vinculados serão movidos automaticamente para o <strong>Grupo Geral</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 1 }}>
          <Button onClick={handleDeleteCancel} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} disableRipple variant="contained" color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TruckGroup;
